import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../context/AuthContext';
import { ArrowLeft, Loader2, Copy, Check, UserMinus, Download, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';

const pctColor = (p) => p >= 70 ? 'text-emerald-600 bg-emerald-50' : p >= 40 ? 'text-amber-600 bg-amber-50' : 'text-rose-600 bg-rose-50';

export default function TeacherClassroomDetailPage() {
    const { classroomId } = useParams();
    const [c, setC] = useState(null);
    const [assignments, setAssignments] = useState([]);
    const [copied, setCopied] = useState(false);

    const reload = () => {
        api.get(`/api/teacher/classrooms/${classroomId}`).then((r) => setC(r.data)).catch(() => setC(false));
        api.get(`/api/teacher/classrooms/${classroomId}/assignments`).then((r) => setAssignments(r.data || [])).catch(() => {});
    };
    useEffect(reload, [classroomId]);

    const remove = async (studentId) => {
        if (!window.confirm('Remove this student from the class?')) return;
        try {
            await api.delete(`/api/teacher/classrooms/${classroomId}/students/${studentId}`);
            toast.success('Student removed');
            reload();
        } catch {
            toast.error('Could not remove student');
        }
    };

    const exportCSV = () => {
        const url = `/api/teacher/classrooms/${classroomId}/export.csv`;
        api.get(url, { responseType: 'blob' }).then((r) => {
            const blob = new Blob([r.data], { type: 'text/csv' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `${c?.name || 'class'}.csv`;
            link.click();
        }).catch(() => toast.error('Export failed'));
    };

    if (c === null) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 text-[#7C3AED] animate-spin" /></div>;
    if (c === false) return <div className="text-sm text-zinc-500">Class not found.</div>;

    return (
        <div className="space-y-6" data-testid="classroom-detail-page">
            <Link to="/teacher/classes" className="inline-flex items-center gap-2 text-sm font-bold text-zinc-500 hover:text-[#7C3AED]" data-testid="back-classes">
                <ArrowLeft className="w-4 h-4" /> All classes
            </Link>

            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-[#7C3AED]">{c.subject}{c.level ? ` · ${c.level}` : ''} {c.unit ? `· ${c.unit}` : ''}</div>
                    <h1 className="text-3xl sm:text-4xl font-black tracking-tight">{c.name}</h1>
                    <div className="text-sm text-zinc-500 mt-1">{c.year_group || '—'} · {c.academic_year || '—'}</div>
                </div>
                <button onClick={exportCSV} className="inline-flex items-center gap-2 bg-zinc-950 text-white font-bold text-xs px-4 py-2 rounded-lg hover:bg-zinc-800" data-testid="export-csv">
                    <Download className="w-3.5 h-3.5" /> Export CSV
                </button>
            </div>

            {/* Join code card */}
            <div className="bg-gradient-to-r from-[#7C3AED] to-[#5B21B6] text-white rounded-2xl p-5">
                <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-white/70">Share to join</div>
                <div className="flex flex-wrap items-baseline gap-4 mt-1">
                    <div>
                        <div className="text-[10px] font-bold uppercase text-white/60">Class code</div>
                        <button onClick={() => { navigator.clipboard.writeText(c.class_code); setCopied(true); setTimeout(() => setCopied(false), 1500); }} className="inline-flex items-center gap-2 font-black text-2xl tracking-wide hover:opacity-90" data-testid="copy-class-code">
                            {c.class_code}
                            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4 opacity-70" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Roster */}
            <div className="bg-white rounded-3xl border border-zinc-200 p-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-black text-base">Students <span className="text-zinc-400 font-normal">({c.students.length})</span></h2>
                </div>
                {c.students.length === 0 ? (
                    <p className="text-sm text-zinc-500 py-8 text-center" data-testid="empty-roster">
                        No students yet — share your class code <strong className="text-[#7C3AED]">{c.class_code}</strong> with them.
                    </p>
                ) : (
                    <div className="divide-y divide-zinc-100">
                        {c.students.map((s) => (
                            <div key={s.id} className="flex items-center justify-between py-3 group" data-testid={`student-row-${s.id}`}>
                                <Link to={`/teacher/classes/${classroomId}/students/${s.id}`} className="flex-1 min-w-0">
                                    <div className="font-bold text-sm truncate group-hover:text-[#7C3AED]">{s.name}</div>
                                    <div className="text-xs text-zinc-500 truncate">{s.email} · joined {s.joined_at ? new Date(s.joined_at).toLocaleDateString() : ''}</div>
                                </Link>
                                <div className="flex items-center gap-3">
                                    <span className="text-xs text-zinc-500">{s.submission_count} subs</span>
                                    <span className={`text-xs font-black px-2 py-1 rounded-md ${pctColor(s.avg_pct || 0)}`}>{Math.round(s.avg_pct || 0)}%</span>
                                    <button onClick={() => remove(s.id)} className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md hover:bg-rose-50 text-rose-600" title="Remove" data-testid={`remove-student-${s.id}`}>
                                        <UserMinus className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Assignments */}
            <AssignmentsSection classroomId={classroomId} assignments={assignments} reload={reload} subject={c.subject} level={c.level} />
        </div>
    );
}

function AssignmentsSection({ classroomId, assignments, reload, subject, level }) {
    const [show, setShow] = useState(false);
    const [form, setForm] = useState({ question_text: '', marks: '', due_date: '' });
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        const text = form.question_text.trim();
        if (!text) return toast.error('Please type the question');
        setSaving(true);
        try {
            const payload = {
                subject: subject || '',
                level: level || 'IAL',
                source: 'manual',
                question_text: text,
                title: text.length > 80 ? text.slice(0, 80) + '…' : text,
                marks: form.marks ? parseInt(form.marks, 10) : null,
                due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
            };
            await api.post(`/api/teacher/classrooms/${classroomId}/assignments`, payload);
            toast.success('Assignment created');
            setShow(false);
            setForm({ question_text: '', marks: '', due_date: '' });
            reload();
        } catch {
            toast.error('Could not create assignment');
        } finally { setSaving(false); }
    };

    return (
        <div className="bg-white rounded-3xl border border-zinc-200 p-6" data-testid="assignments-section">
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-black text-base inline-flex items-center gap-2"><ClipboardList className="w-4 h-4 text-[#7C3AED]" /> Assignments</h2>
                <button onClick={() => setShow(!show)} className="text-xs font-bold text-[#7C3AED]" data-testid="toggle-new-assignment">
                    {show ? 'Cancel' : '+ New assignment'}
                </button>
            </div>
            {show && (
                <form onSubmit={submit} className="space-y-3 mb-4 p-4 bg-[#F8F7FC] rounded-xl" data-testid="assignment-form">
                    <div>
                        <label className="text-[10px] font-bold text-zinc-600 uppercase tracking-wider mb-1.5 block">Question</label>
                        <textarea
                            rows={4}
                            placeholder="Paste or type the full question here. The AI will detect the topic and marks automatically from past papers."
                            value={form.question_text}
                            onChange={(e) => setForm({ ...form, question_text: e.target.value })}
                            className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 outline-none focus:border-[#7C3AED] text-sm"
                            data-testid="assign-question-text"
                            autoFocus
                        />
                        <p className="text-[11px] text-zinc-500 mt-1">Tip: you can also import a question from the <strong>Question Bank</strong> or generate one in <strong>Create</strong>.</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <input
                            type="number" min="1" max="40"
                            placeholder="Marks (optional)"
                            value={form.marks}
                            onChange={(e) => setForm({ ...form, marks: e.target.value })}
                            className="px-3 py-2 rounded-lg border border-zinc-200 text-xs"
                            data-testid="assign-marks"
                        />
                        <input
                            type="date"
                            value={form.due_date}
                            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                            className="px-3 py-2 rounded-lg border border-zinc-200 text-xs"
                            data-testid="assign-due"
                        />
                    </div>
                    <button disabled={saving} className="w-full bg-[#7C3AED] text-white text-sm font-bold py-2.5 rounded-lg hover:bg-[#6D28D9] disabled:opacity-60" data-testid="assign-submit">
                        {saving ? 'Assigning…' : 'Assign to class'}
                    </button>
                </form>
            )}
            {assignments.length === 0 ? (
                <p className="text-xs text-zinc-500 text-center py-4">No assignments yet.</p>
            ) : (
                <ul className="divide-y divide-zinc-100">
                    {assignments.map((a) => {
                        const label = a.title
                            || (a.question_text
                                ? (a.question_text.length > 80 ? a.question_text.slice(0, 80) + '…' : a.question_text)
                                : (a.paper_year ? `Paper ${a.paper_year} ${a.series || ''} · Q${a.question_number || ''}` : 'Class question'));
                        const sourceTag = a.source === 'question_bank' ? 'From Question Bank'
                            : a.source === 'create' ? 'AI-Created'
                            : 'Manual';
                        return (
                            <li key={a.id} className="py-3 flex items-start justify-between gap-3 text-sm" data-testid={`assignment-${a.id}`}>
                                <div className="min-w-0 flex-1">
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-[#7C3AED]">
                                        {sourceTag}{a.marks ? ` · ${a.marks} marks` : ''}
                                    </div>
                                    <div className="font-bold text-zinc-900 line-clamp-2 break-words">{label}</div>
                                    <div className="text-xs text-zinc-500 mt-0.5">Due {a.due_date ? new Date(a.due_date).toLocaleDateString() : '—'}</div>
                                </div>
                                <span className="text-xs font-bold text-zinc-500 whitespace-nowrap flex-shrink-0 pt-0.5">{a.submitted_count}/{a.roster_count} submitted</span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
